/**
 * Shared Style Constants - CRM Standard Design System
 * Notion-like compact, modern design
 */

/**
 * Page header styles
 */
export const pageStyles = {
  /** Page container spacing */
  container: 'space-y-3',
  /** Page title - main heading */
  title: 'text-base font-semibold text-gray-900',
  /** Page subtitle/description */
  subtitle: 'text-[11px] text-gray-500',
  /** Header wrapper */
  header: 'flex items-center justify-between gap-3',
  /** Header left section with title */
  headerLeft: 'flex items-center gap-2 min-w-0',
  /** Back button style */
  backButton: 'p-1 -ml-1 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700',
} as const;

/**
 * Card styles
 */
export const cardStyles = {
  /** Standard card container */
  container: 'bg-white rounded-lg border border-gray-200',
  /** Card with shadow */
  containerShadow: 'bg-white rounded-lg border border-gray-200 shadow-sm',
  /** Card header section */
  header: 'px-3 py-2.5 border-b border-gray-100',
  /** Card header with background */
  headerBg: 'px-3 py-2.5 border-b border-gray-100 bg-gray-50/50',
  /** Card content */
  content: 'p-3',
  /** Card content compact */
  contentCompact: 'p-2',
  /** Card footer */
  footer: 'px-3 py-2 border-t border-gray-100',
  /** Card footer with background */
  footerBg: 'px-3 py-2 border-t border-gray-100 bg-gray-50/50',
} as const;

/**
 * Table styles
 */
export const tableStyles = {
  /** Table container */
  container: 'rounded-lg border border-gray-200 bg-white overflow-hidden',
  /** Table header row */
  headerRow: 'border-b border-gray-100 bg-gray-50/50',
  /** Table header cell */
  headerCell: 'px-3 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider',
  /** Table header cell sortable */
  headerCellSortable: 'px-3 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none',
  /** Table body */
  body: 'divide-y divide-gray-100',
  /** Table row */
  row: 'hover:bg-blue-50/30 transition-colors',
  /** Table row selected */
  rowSelected: 'bg-blue-50',
  /** Table row clickable */
  rowClickable: 'cursor-pointer',
  /** Table cell */
  cell: 'px-3 py-2 text-xs text-gray-900',
  /** Table cell secondary text */
  cellSecondary: 'text-xs text-gray-500',
} as const;

/**
 * Button styles (use with cn() helper)
 */
export const buttonStyles = {
  /** Primary action button */
  primary: 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors',
  /** Secondary/outline button */
  secondary: 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors',
  /** Ghost button */
  ghost: 'inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors',
  /** Icon button */
  icon: 'p-1.5 rounded hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700',
  /** Icon button small */
  iconSm: 'p-1 rounded hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700',
} as const;

/**
 * Badge/Status styles
 */
export const badgeStyles = {
  /** Standard badge base */
  base: 'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium',
  /** Badge variants */
  default: 'bg-gray-100 text-gray-700',
  primary: 'bg-blue-50 text-blue-700',
  success: 'bg-emerald-50 text-emerald-700',
  warning: 'bg-amber-50 text-amber-700',
  danger: 'bg-red-50 text-red-700',
  info: 'bg-cyan-50 text-cyan-700',
  purple: 'bg-violet-50 text-violet-700',
  orange: 'bg-orange-50 text-orange-700',
} as const;

/**
 * Form/Input styles
 */
export const formStyles = {
  /** Input field */
  input: 'h-8 text-sm px-2.5 rounded-md border border-gray-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-400',
  /** Input with search icon */
  inputWithIcon: 'h-8 text-sm pl-8 pr-2.5 rounded-md border border-gray-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-400',
  /** Search icon positioning */
  searchIcon: 'absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400',
  /** Label */
  label: 'text-xs font-medium text-gray-700',
  /** Helper text */
  helper: 'text-[11px] text-gray-500 mt-1',
  /** Error text */
  error: 'text-[11px] text-red-600 mt-1',
} as const;

/**
 * Icon sizes - standardized across the app
 */
export const iconSizes = {
  /** Extra small - for badges, pills */
  xs: 'w-3 h-3',
  /** Small - for compact UI, inline */
  sm: 'w-3.5 h-3.5',
  /** Medium - standard content */
  md: 'w-4 h-4',
  /** Large - headers, navigation */
  lg: 'w-5 h-5',
} as const;

/**
 * Spacing constants
 */
export const spacing = {
  /** Page level spacing */
  page: 'space-y-4',
  /** Section spacing */
  section: 'space-y-3',
  /** List item spacing */
  list: 'space-y-1',
  /** Compact list spacing */
  listCompact: 'space-y-0.5',
  /** Grid gap */
  grid: 'gap-3',
  /** Flex gap standard */
  flex: 'gap-2',
  /** Flex gap compact */
  flexCompact: 'gap-1.5',
} as const;

// Legacy statusColors, statusLabels, getStatusBadgeClass, getStatusLabel removed.
// Use workflow-driven colors/labels via useWorkflowConfig hooks and WorkflowStatusBadge component.

/**
 * Empty state styles
 */
export const emptyStateStyles = {
  container: 'flex flex-col items-center justify-center py-8 text-center',
  icon: 'w-10 h-10 text-gray-300 mb-3',
  title: 'text-sm font-medium text-gray-900 mb-1',
  description: 'text-xs text-gray-500 mb-3 max-w-xs',
} as const;

/**
 * Error state styles
 */
export const errorStateStyles = {
  container: 'flex flex-col items-center justify-center rounded-lg border border-red-200 bg-red-50 p-6 text-center',
  icon: 'h-8 w-8 text-red-500 mb-3',
  title: 'text-sm font-medium text-red-800 mb-1',
  description: 'text-xs text-red-600 mb-3 max-w-xs',
} as const;

/**
 * Loading skeleton styles
 */
export const skeletonStyles = {
  base: 'animate-pulse bg-gray-100 rounded',
  text: 'h-3 bg-gray-100 rounded animate-pulse',
  textLg: 'h-4 bg-gray-100 rounded animate-pulse',
  avatar: 'w-8 h-8 bg-gray-100 rounded-full animate-pulse',
  button: 'h-8 w-20 bg-gray-100 rounded animate-pulse',
} as const;

/**
 * Notion-like detail page styles
 */
export const detailStyles = {
  /** Main container for detail pages */
  container: 'space-y-4',
  /** Two-column layout for detail pages */
  grid: 'grid gap-4 lg:grid-cols-3',
  /** Main content area (2 cols) */
  main: 'lg:col-span-2 space-y-3',
  /** Sidebar area (1 col) */
  sidebar: 'space-y-3',
  /** Section container - Notion style */
  section: 'bg-white rounded-lg border border-gray-200',
  /** Section header */
  sectionHeader: 'px-3 py-2 border-b border-gray-100 flex items-center justify-between',
  /** Section title */
  sectionTitle: 'text-xs font-medium text-gray-900 uppercase tracking-wide',
  /** Section content */
  sectionContent: 'divide-y divide-gray-50',
  /** Section content with padding */
  sectionContentPadded: 'p-3 space-y-2',
} as const;

/**
 * Property row styles - Notion-like key-value display
 */
export const propertyStyles = {
  /** Property row container */
  row: 'flex items-start gap-3 px-3 py-2 hover:bg-gray-50/50 transition-colors',
  /** Property row compact */
  rowCompact: 'flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50/50 transition-colors',
  /** Property label */
  label: 'w-28 shrink-0 text-[11px] text-gray-500 pt-0.5',
  /** Property label compact */
  labelCompact: 'w-24 shrink-0 text-[11px] text-gray-500',
  /** Property value */
  value: 'flex-1 text-xs text-gray-900 min-w-0',
  /** Property value with icon */
  valueWithIcon: 'flex-1 flex items-center gap-1.5 text-xs text-gray-900 min-w-0',
  /** Empty value */
  valueEmpty: 'flex-1 text-xs text-gray-400 italic',
  /** Property group - horizontal layout */
  group: 'grid grid-cols-2 gap-x-4',
} as const;

/**
 * Compact list styles - for inline lists and data
 */
export const listStyles = {
  /** List container */
  container: 'divide-y divide-gray-100',
  /** List item */
  item: 'flex items-center justify-between gap-3 px-3 py-2 hover:bg-gray-50/50 transition-colors',
  /** List item compact */
  itemCompact: 'flex items-center justify-between gap-2 px-2 py-1.5 hover:bg-gray-50/50 transition-colors',
  /** Item primary text */
  itemPrimary: 'text-xs font-medium text-gray-900 truncate',
  /** Item secondary text */
  itemSecondary: 'text-[11px] text-gray-500 truncate',
  /** Item meta (right side) */
  itemMeta: 'text-[11px] text-gray-500 shrink-0',
} as const;

/**
 * Timeline styles - for activity feeds
 */
export const timelineStyles = {
  /** Timeline container */
  container: 'relative',
  /** Timeline line */
  line: 'absolute left-3 top-0 bottom-0 w-px bg-gray-200',
  /** Timeline item */
  item: 'relative flex gap-3 pb-3',
  /** Timeline dot */
  dot: 'relative z-10 w-6 h-6 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center shrink-0',
  /** Timeline dot active */
  dotActive: 'relative z-10 w-6 h-6 rounded-full bg-blue-500 border-2 border-blue-500 flex items-center justify-center shrink-0',
  /** Timeline content */
  content: 'flex-1 min-w-0 pt-0.5',
  /** Timeline title */
  title: 'text-xs font-medium text-gray-900',
  /** Timeline description */
  description: 'text-[11px] text-gray-500 mt-0.5',
  /** Timeline time */
  time: 'text-[10px] text-gray-400 mt-0.5',
} as const;

/**
 * Action button group styles
 */
export const actionStyles = {
  /** Action group container */
  group: 'flex items-center gap-1.5',
  /** Action group with divider */
  groupDivided: 'flex items-center gap-1.5 divide-x divide-gray-200',
  /** Dropdown trigger */
  trigger: 'p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors',
} as const;

/**
 * Kanban/Board styles
 */
export const boardStyles = {
  /** Board container - responsive: stack on mobile, scroll on tablet+ */
  container: 'flex flex-col gap-3 pb-4 min-h-0 md:flex-row md:overflow-x-auto scrollbar-hide',
  /** Column - full width on mobile, fixed width on tablet+ */
  column: 'w-full shrink-0 bg-gray-50 rounded-lg md:w-56 lg:w-64',
  /** Column header */
  columnHeader: 'px-3 py-2 flex items-center justify-between',
  /** Column title */
  columnTitle: 'text-xs font-medium text-gray-700',
  /** Column count badge */
  columnCount: 'text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-200 text-gray-600',
  /** Column content - collapsible on mobile */
  columnContent: 'px-2 pb-2 space-y-2 max-h-64 overflow-y-auto md:max-h-[calc(100vh-200px)] scrollbar-hide',
  /** Card in column */
  card: 'bg-white rounded-md border border-gray-200 p-2.5 hover:border-gray-300 hover:shadow-sm transition-all cursor-pointer',
  /** Card title */
  cardTitle: 'text-xs font-medium text-gray-900 truncate',
  /** Card subtitle */
  cardSubtitle: 'text-[11px] text-gray-500 truncate mt-0.5',
  /** Card meta row */
  cardMeta: 'flex items-center gap-2 mt-2 text-[10px] text-gray-500',
} as const;

/**
 * Modal/Dialog compact styles
 */
export const modalStyles = {
  /** Modal header */
  header: 'px-4 py-3 border-b border-gray-100',
  /** Modal title */
  title: 'text-sm font-semibold text-gray-900',
  /** Modal description */
  description: 'text-xs text-gray-500 mt-0.5',
  /** Modal content */
  content: 'px-4 py-3 space-y-3',
  /** Modal footer */
  footer: 'px-4 py-3 border-t border-gray-100 flex items-center justify-end gap-2',
} as const;

/**
 * Stats/Metric card styles
 */
export const statStyles = {
  /** Grid container for stat cards */
  grid: 'grid grid-cols-2 md:grid-cols-4 gap-3',
  /** Stat card */
  card: 'bg-white rounded-lg border border-gray-200 p-3',
  /** Stat card header with label and icon */
  header: 'flex items-center justify-between',
  /** Stat label */
  label: 'text-[11px] text-gray-500 font-medium',
  /** Stat value */
  value: 'text-lg font-semibold text-gray-900 mt-0.5',
  /** Stat description/subtitle */
  description: 'text-[10px] text-gray-400 mt-0.5',
  /** Stat change positive */
  changePositive: 'text-[10px] text-emerald-600 mt-1',
  /** Stat change negative */
  changeNegative: 'text-[10px] text-red-600 mt-1',
  /** Stat icon (small) */
  icon: 'h-3.5 w-3.5 text-gray-400',
  /** Stat icon container (larger) */
  iconContainer: 'w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500',
} as const;
